<script setup lang="ts">
import { goSolidApi } from "@/composables/useGoSolidApi";
import AddGrexelUserModal from "@/registry/admin/components/AddGrexelUserModal.vue";
import { useRegistryStatus } from "@/registry/useRegistryStatus";
import { format } from "date-fns";
import { computed, onMounted, ref } from "vue";
import { useAccount } from "@/composables/account/useAccount";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import { reachesAccessClass } from "@/composables/useAdminCheck";

const { globalErrorHandler, globalSuccessHandler } = useGlobalHandlers();
const { accountData, loaded } = useAccount();
const isSyncing = ref(false);
const isSyncingNewCompanies = ref(false);
const isUpdatingMultipliers = ref(false);
const isReloadingSecrets = ref(false);

// Transfer modal state
const showTransferModal = ref(false);
const transferForm = ref({
  integration: "",
  certificateId: "",
  fromAccount: "",
  toAccount: "",
});

// Add Grexel User modal state
const showAddUserModal = ref(false);

const {
  integrationStatus,
  isCheckingStatus,
  isTestingAuth,
  isTestingCompanies,
  isTestingDevices,
  isTestingCertificates,
  isTestingIssuanceHistory,
  isTestingTransfer,
  isSyncingCompanies,
  fetchRegistriesData,
  checkRegistryStatus,
  testRegistryAuth,
  testRegistryCompanies,
  testRegistryDevices,
  testRegistryCertificates,
  testRegistryIssuanceHistory,
  testRegistryTransfer,
  syncCompaniesFromRegistry,
} = useRegistryStatus();

const canReloadSecrets = computed(
  () => loaded.value && reachesAccessClass(accountData, "SUPER")
);

const triggerBackgroundSync = async () => {
  try {
    isSyncing.value = true;
    await goSolidApi.post("/api/admin/registry/sync");
  } catch (error) {
    globalErrorHandler(error);
  } finally {
    isSyncing.value = false;
  }
};

const triggerBackgroundSyncNewCompanies = async () => {
  try {
    isSyncingNewCompanies.value = true;
    await goSolidApi.post("/api/admin/registry/sync-new-companies");
  } catch (error) {
    globalErrorHandler(error);
  } finally {
    isSyncingNewCompanies.value = false;
  }
};
const reloadRegistryAgentSecrets = async () => {
  try {
    isReloadingSecrets.value = true;
    const response = await goSolidApi.post<{ status: string }>(
      "/api/admin/registry/secrets/reload"
    );
    globalSuccessHandler(
      "Secrets Reloaded",
      `Registry agent reloaded its secrets from Secret Manager (status: ${response.data.status}).`
    );
  } catch (error) {
    globalErrorHandler(error);
  } finally {
    isReloadingSecrets.value = false;
  }
};

const updateDeviceMultipliers = async (integrationName?: string) => {
  try {
    isUpdatingMultipliers.value = true;
    let url = "/api/admin/update-device-multipliers";
    if (integrationName) {
      url += `?integration_name=${encodeURIComponent(integrationName)}`;
    }
    const response = await goSolidApi.post(url);

    if (response.data.success) {
      globalSuccessHandler(
        "Update Started",
        response.data.message ||
          "Device multiplier update task started successfully"
      );
    } else {
      globalErrorHandler({
        message: `Failed to start device multiplier update: ${response.data.message}`,
        code: "MULTIPLIER_UPDATE_ERROR",
      });
    }
  } catch (error: any) {
    globalErrorHandler(error);
  } finally {
    isUpdatingMultipliers.value = false;
  }
};

const handleSyncCompaniesFromRegistry = async (integrationName: string) => {
  try {
    const result = await syncCompaniesFromRegistry(integrationName);
    globalSuccessHandler(
      "Sync Started",
      result.message ??
        `Company sync from ${integrationName} triggered successfully`
    );
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (error) {
    // Error already handled by composable
  }
};

const formatDate = (dateStr: string | null): string => {
  if (!dateStr) return "Never";
  return format(new Date(dateStr), "yyyy-MM-dd HH:mm:ss") + " UTC";
};

// Add Grexel User handlers
const openAddUserModal = () => {
  showAddUserModal.value = true;
};

// Test operation handlers
const runProbe = async (
  integrationName: string,
  operation: string,
  probe: () => Promise<any>
) => {
  try {
    showTestResult(integrationName, operation, await probe());
  } catch {
    // Error already handled by composable
  }
};

const runCompanyProbe = async (
  integrationName: string,
  operation: string,
  probe: (registryCompanyId: string) => Promise<any>
) => {
  const registryCompanyId = prompt(
    `Enter registry company ID for ${integrationName} ${operation.toLowerCase()} test:`
  );
  if (!registryCompanyId) return;

  await runProbe(integrationName, operation, () => probe(registryCompanyId));
};

const handleTestAuth = (integrationName: string) =>
  runProbe(integrationName, "Authentication", () =>
    testRegistryAuth(integrationName)
  );

const handleTestCompanies = (integrationName: string) =>
  runProbe(integrationName, "Companies", () =>
    testRegistryCompanies(integrationName)
  );

const handleTestDevices = (integrationName: string) =>
  runCompanyProbe(integrationName, "Devices", (registryCompanyId) =>
    testRegistryDevices(integrationName, registryCompanyId)
  );

const handleTestCertificates = (integrationName: string) =>
  runCompanyProbe(integrationName, "Certificates", (registryCompanyId) =>
    testRegistryCertificates(integrationName, registryCompanyId)
  );

const handleTestIssuanceHistory = (integrationName: string) =>
  runCompanyProbe(integrationName, "Issuance History", (registryCompanyId) =>
    testRegistryIssuanceHistory(integrationName, registryCompanyId)
  );

const openTransferModal = (integrationName: string) => {
  transferForm.value = {
    integration: integrationName,
    certificateId: "",
    fromAccount: "",
    toAccount: "",
  };
  showTransferModal.value = true;
};

const handleTestTransfer = async () => {
  if (
    !transferForm.value.certificateId ||
    !transferForm.value.fromAccount ||
    !transferForm.value.toAccount
  ) {
    globalErrorHandler({
      message: "Please fill in all fields",
      code: "VALIDATION_ERROR",
    });
    return;
  }

  try {
    const result = await testRegistryTransfer(
      transferForm.value.integration,
      transferForm.value.certificateId,
      transferForm.value.fromAccount,
      transferForm.value.toAccount
    );
    showTestResult(transferForm.value.integration, "Transfer", result);
    showTransferModal.value = false;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (error) {
    // Error already handled by composable
  }
};

const showTestResult = (
  integrationName: string,
  operation: string,
  result: any
) => {
  if (result.success) {
    let message = `${operation} test for ${integrationName}: ${result.message}`;
    if (result.count !== undefined) {
      message += `\n\nCount: ${result.count}`;
    }
    if (result.data) {
      message += `\n\nData:\n${JSON.stringify(result.data, null, 2)}`;
    }
    globalSuccessHandler("Test Successful", message);
  } else {
    globalErrorHandler({
      message: `${operation} test for ${integrationName} failed:\n${result.error}`,
      code: "TEST_FAILURE",
    });
  }
};

onMounted(fetchRegistriesData);
</script>

<template>
  <div id="AdminRegistries" class="container">
    <div class="row justify-content-between align-items-center mb-3 mt-4">
      <div class="col-auto">
        <h1>Registry Integrations</h1>
      </div>
      <div class="col d-flex gap-3 justify-content-end align-items-center">
        <button class="btn btn-info" @click="checkRegistryStatus()">
          Check All Statuses
        </button>
        <button
          class="btn btn-primary"
          @click="triggerBackgroundSyncNewCompanies"
          :disabled="isSyncingNewCompanies"
        >
          <span
            v-if="isSyncingNewCompanies"
            class="spinner-border spinner-border-sm me-2"
            role="status"
          ></span>
          {{
            isSyncingNewCompanies
              ? "Triggering..."
              : "Sync NEW companies from all registries"
          }}
        </button>
        <button
          class="btn btn-primary"
          @click="triggerBackgroundSync"
          :disabled="isSyncing"
        >
          <span
            v-if="isSyncing"
            class="spinner-border spinner-border-sm me-2"
            role="status"
          ></span>
          {{
            isSyncing
              ? "Triggering..."
              : "Trigger registry sync of all companies"
          }}
        </button>

        <button
          class="btn btn-outline-secondary"
          @click="updateDeviceMultipliers()"
          :disabled="isUpdatingMultipliers"
        >
          <span
            v-if="isUpdatingMultipliers"
            class="spinner-border spinner-border-sm me-2"
            role="status"
          ></span>
          {{
            isUpdatingMultipliers
              ? "Updating..."
              : "Update All Device Multipliers"
          }}
        </button>

        <button
          v-if="canReloadSecrets"
          class="btn btn-outline-warning"
          @click="reloadRegistryAgentSecrets"
          :disabled="isReloadingSecrets"
        >
          <span
            v-if="isReloadingSecrets"
            class="spinner-border spinner-border-sm me-2"
            role="status"
          ></span>
          {{ isReloadingSecrets ? "Reloading..." : "Reload Agent Secrets" }}
        </button>
      </div>
    </div>

    <div class="card">
      <div class="card-body">
        <div class="table-responsive">
          <table class="table">
            <thead>
              <tr>
                <th>Integration</th>
                <th>Credentials last saved at</th>
                <th>Saved by</th>
                <th>Status</th>
                <th>Actions</th>
                <th>Local testing</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(data, name) in integrationStatus" :key="name">
                <td>{{ name }}</td>
                <td>{{ formatDate(data.last_saved_at) }}</td>
                <td>{{ data.saved_by }}</td>
                <td>
                  <span v-if="data.online === true" class="badge bg-success"
                    >Online</span
                  >
                  <span
                    v-else-if="data.online === false"
                    class="badge bg-danger"
                    >Offline</span
                  >
                  <span v-else class="badge bg-secondary">Unknown</span>
                </td>
                <td>
                  <div class="btn-group-vertical gap-1" role="group">
                    <!-- Check Status -->
                    <button
                      class="btn btn-sm btn-outline-primary"
                      @click="checkRegistryStatus(name)"
                      :disabled="isCheckingStatus[name]"
                    >
                      <span
                        v-if="isCheckingStatus[name]"
                        class="spinner-border spinner-border-sm"
                        role="status"
                      ></span>
                      <span v-else>Check Status</span>
                    </button>

                    <!-- Sync Companies from Registry -->
                    <button
                      class="btn btn-sm btn-outline-primary"
                      @click="handleSyncCompaniesFromRegistry(String(name))"
                      :disabled="isSyncingCompanies[name]"
                    >
                      <span
                        v-if="isSyncingCompanies[name]"
                        class="spinner-border spinner-border-sm"
                        role="status"
                      ></span>
                      <span v-else>Sync Companies</span>
                    </button>

                    <!-- Add Grexel User (only for GREXEL) -->
                    <button
                      v-if="String(name) === 'GREXEL'"
                      class="btn btn-sm btn-outline-success"
                      @click="openAddUserModal"
                    >
                      Add Grexel User
                    </button>

                    <!-- Update Device Multipliers for this integration (only for supported integrations) -->
                    <button
                      v-if="
                        ['GREXEL', 'CESAR', 'ELERING'].includes(String(name))
                      "
                      class="btn btn-sm btn-outline-secondary"
                      @click="updateDeviceMultipliers(String(name))"
                      :disabled="isUpdatingMultipliers"
                    >
                      <span
                        v-if="isUpdatingMultipliers"
                        class="spinner-border spinner-border-sm"
                        role="status"
                      ></span>
                      <span v-else>Update Multipliers</span>
                    </button>
                  </div>
                </td>
                <td>
                  <div class="btn-group-vertical gap-1" role="group">
                    <!-- Check Get Companies -->
                    <button
                      class="btn btn-sm btn-outline-info"
                      @click="handleTestCompanies(name)"
                      :disabled="isTestingCompanies[name]"
                    >
                      <span
                        v-if="isTestingCompanies[name]"
                        class="spinner-border spinner-border-sm"
                        role="status"
                      ></span>
                      <span v-else>Check Get Companies</span>
                    </button>

                    <!-- Check Get Devices -->
                    <button
                      class="btn btn-sm btn-outline-info"
                      @click="handleTestDevices(name)"
                      :disabled="isTestingDevices[name]"
                    >
                      <span
                        v-if="isTestingDevices[name]"
                        class="spinner-border spinner-border-sm"
                        role="status"
                      ></span>
                      <span v-else>Check Get Devices</span>
                    </button>

                    <!-- Check Get Certificates -->
                    <button
                      class="btn btn-sm btn-outline-info"
                      @click="handleTestCertificates(name)"
                      :disabled="isTestingCertificates[name]"
                    >
                      <span
                        v-if="isTestingCertificates[name]"
                        class="spinner-border spinner-border-sm"
                        role="status"
                      ></span>
                      <span v-else>Check Get Certificates</span>
                    </button>

                    <!-- Get Issuance History -->
                    <button
                      class="btn btn-sm btn-outline-info"
                      @click="handleTestIssuanceHistory(name)"
                      :disabled="isTestingIssuanceHistory[name]"
                    >
                      <span
                        v-if="isTestingIssuanceHistory[name]"
                        class="spinner-border spinner-border-sm"
                        role="status"
                      ></span>
                      <span v-else>Get Issuance History</span>
                    </button>

                    <!-- Test Transfer Certificate -->
                    <button
                      class="btn btn-sm btn-outline-warning"
                      @click="openTransferModal(name)"
                      :disabled="isTestingTransfer[name]"
                    >
                      <span
                        v-if="isTestingTransfer[name]"
                        class="spinner-border spinner-border-sm"
                        role="status"
                      ></span>
                      <span v-else>Test Transfer Certificate</span>
                    </button>

                    <!-- Run Auth -->
                    <button
                      class="btn btn-sm btn-outline-warning"
                      @click="handleTestAuth(name)"
                      :disabled="isTestingAuth[name]"
                    >
                      <span
                        v-if="isTestingAuth[name]"
                        class="spinner-border spinner-border-sm"
                        role="status"
                      ></span>
                      <span v-else>Run Auth</span>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Transfer Test Modal -->
    <div v-if="showTransferModal" class="modal d-block" tabindex="-1">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              Test Certificate Transfer - {{ transferForm.integration }}
            </h5>
            <button
              type="button"
              class="btn-close"
              @click="showTransferModal = false"
            ></button>
          </div>
          <div class="modal-body">
            <form @submit.prevent="handleTestTransfer">
              <div class="mb-3">
                <label for="certificateId" class="form-label"
                  >Certificate ID</label
                >
                <input
                  type="text"
                  class="form-control"
                  id="certificateId"
                  v-model="transferForm.certificateId"
                  placeholder="Enter certificate ID to test transfer"
                  required
                />
              </div>

              <div class="mb-3">
                <label for="fromAccount" class="form-label">From Account</label>
                <input
                  type="text"
                  class="form-control"
                  id="fromAccount"
                  v-model="transferForm.fromAccount"
                  placeholder="Enter source account ID/EECS"
                  required
                />
              </div>

              <div class="mb-3">
                <label for="toAccount" class="form-label">To Account</label>
                <input
                  type="text"
                  class="form-control"
                  id="toAccount"
                  v-model="transferForm.toAccount"
                  placeholder="Enter destination account ID/EECS"
                  required
                />
              </div>
            </form>
          </div>
          <div class="modal-footer">
            <button
              type="button"
              class="btn btn-secondary"
              @click="showTransferModal = false"
            >
              Cancel
            </button>
            <button
              type="button"
              class="btn btn-primary"
              @click="handleTestTransfer"
              :disabled="isTestingTransfer[transferForm.integration]"
            >
              <span
                v-if="isTestingTransfer[transferForm.integration]"
                class="spinner-border spinner-border-sm me-2"
                role="status"
              ></span>
              Test Transfer
            </button>
          </div>
        </div>
      </div>
    </div>
    <div v-if="showTransferModal" class="modal-backdrop fade show"></div>

    <!-- Add Grexel User Modal -->
    <AddGrexelUserModal
      v-model="showAddUserModal"
      @close="showAddUserModal = false"
    />
  </div>
</template>
